import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";

import OnTimePickupCard from "../performance/OnTimePickupCard";
import OnTimeDeliveryCard from "../performance/OnTimeDeliveryCard";
import FirstTenderComplianceCard from "../performance/FirstTenderComplianceCard";
import LaneAwardComplianceCard from "../performance/LaneAwardComplianceCard";
import BillingAccuracyCard from "../performance/BillingAccuracyCard";
import FourKitesTrackingConsistencyCard from "../performance/FourKitesTrackingConsistencyCard";
import CarrierEventResponseEfficencyCard from "../performance/CarrierEventResponseEfficencyCard";
import TrailerPoolComplianceCard from "../performance/TrailerPoolComplianceCard";
import CarrierSelectionDialog from "../performance/CarrierSelectionDialog";
import CarrierPerformanceHeader from "../performance/CarrierPerformanceHeader";
import HeaderAPIHandler from "../common/HeaderAPIHandler";

import "./CarrierPerformance.sass";

const CarrierPerformance = () => {
    const { carrierid } = useParams();
    const { t } = useTranslation();

    const carrierServiceMetadata = useSelector((state) => state.carrierMetadata?.carrierServiceMetadata) || [];
    const isCarrierServiceMetadataLoading = useSelector((state) => state.carrierMetadata?.isCarrierServiceMetadataLoading);
    const locMetadata = useSelector((state) => state.locationMetadata?.locMetadata) || [];

    // Dialog open state as boolean (simpler)
    const [openSelectCarrier, setOpenSelectCarrier] = useState(false);

    // Build a Set for fast validity checks
    const carrierCodeSet = useMemo(() => {
        return new Set(carrierServiceMetadata.map((c) => c.carrierCode));
    }, [carrierServiceMetadata]);

    const isMetadataReady = locMetadata.length > 0 && carrierServiceMetadata.length > 0;

    const isValidCarrier = useMemo(() => {
        if (!carrierid) return false;
        return carrierCodeSet.has(carrierid);
    }, [carrierid, carrierCodeSet]);

    // Define loading that the cards can rely on
    const carrierLoading = !carrierid || isCarrierServiceMetadataLoading;

    // Open dialog only when metadata is ready AND (no carrier selected OR invalid carrier)
    useEffect(() => {
        if (!isMetadataReady) return;

        const needsSelection = !carrierid || !isValidCarrier;
        setOpenSelectCarrier(needsSelection);
    }, [isMetadataReady, carrierid, isValidCarrier]);

    const renderPlaceholder = () => (
        <Grid className="placeholderCard" data-testid="no-carriers-access">
            {isCarrierServiceMetadataLoading
                ? t("loading")
                : t("no_access")}
        </Grid>
    );

    // Determine if we should display the "No Access" placeholder.
    // This happens when:
    // - The URL includes a carrier ID (so the user explicitly tried to view a carrier), BUT
    // - That carrier ID is not in the user's allowed carrier list (isInvalid).
    // If no carrier ID is provided at all, we do NOT show "No Access"; instead the dialog opens.
    const shouldShowNoAccess = !!carrierid && !isValidCarrier;

    return (
        <div className="performanceContainer" data-testid="performance-page-container">
            {shouldShowNoAccess ? (
                renderPlaceholder()
            ) : (
                <>
                    <HeaderAPIHandler />

                    <Grid container data-testid="performance-main-grid">
                        <Grid item xs={12} className="performanceHeader" data-testid="performance-header-section">
                            <CarrierPerformanceHeader />
                        </Grid>

                        <Grid item xs={12} className="segments" data-testid="segment-ontime">
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={12} lg={6}>
                                    <OnTimePickupCard carrierLoading={carrierLoading} />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={6}>
                                    <OnTimeDeliveryCard carrierLoading={carrierLoading} />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} className="segments" data-testid="segment-compliance">
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={12} md={12} lg={6}>
                                    <FirstTenderComplianceCard carrierLoading={carrierLoading} />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={6}>
                                    <LaneAwardComplianceCard carrierLoading={carrierLoading} />
                                    <FourKitesTrackingConsistencyCard carrierLoading={carrierLoading} />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} className="segments" data-testid="segment-accuracy-efficiency">
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={12} md={12} lg={6}>
                                    <CarrierEventResponseEfficencyCard carrierLoading={carrierLoading} />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={6}>
                                    <BillingAccuracyCard carrierLoading={carrierLoading} />
                                    <TrailerPoolComplianceCard carrierLoading={carrierLoading} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Show dialog when there is no valid carrier context */}
                    {isMetadataReady && (
                        <CarrierSelectionDialog
                            data-testid="carrier-selection-dialog"
                            openSelectCarrier={openSelectCarrier}
                            setOpenSelectCarrier={setOpenSelectCarrier}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default CarrierPerformance;